import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { CreatePostFormData, FilterPostsInputs, PostWithUser } from "./types/index.type";
import { buildPostQuery } from "./utils/buildPostQuery";
import { postRepository } from "./repositories/post.repository";
import { userRepository } from "../users/repositories/user.repository";
import { generateDocId } from "src/utils/firebase.util";
import { uploadFilesToFirebase } from "src/firebase";
import { BasePost, Post } from "./types/post.type";
import { Timestamp } from "firebase/firestore";
import { UserLangCode } from "src/utils/util.types";
import { TAALY } from "src/utils/constants.util";
import { callFunctionSafe } from "src/firebase/functions";
import { ScheduledPost } from "./types/scheduled-posts.entity";
import { scheduledPostRepository } from "./repositories/scheduled-post.repository";

const postService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getPostById: builder.query<Post | ScheduledPost | null, { id: string; isScheduled: boolean }>({
      queryFn: ({ id, isScheduled }) =>
        executeQuery(async () =>
          isScheduled ? await scheduledPostRepository.findById(id) : await postRepository.findById(id)
        ),
    }),
    getPosts: builder.query<PostWithUser[] | ScheduledPost[], { where: FilterPostsInputs; offset: string }>({
      queryFn: ({ where, offset }) =>
        executeQuery(async () => {
          if (where.isScheduled) {
            return await scheduledPostRepository.find({ offset });
          }
          const filter = buildPostQuery(where);

          const posts = await postRepository.find({ where: filter, offset, orderBy: ["createdAt", "desc"] });

          const postsUsersIds = posts.map((post) => post.userId).filter((userId) => userId !== TAALY);

          const users = await userRepository.findByIds(postsUsersIds);

          const postsWithUsers = posts.map((post) => {
            const user = users.find((u) => u.id === post.userId);
            return {
              id: post.id,
              post,
              user,
            };
          });

          return postsWithUsers;
        }),
      providesTags: ["Post"],
    }),
    editScheduledPost: builder.mutation<void, Partial<ScheduledPost>>({
      queryFn: async (newPost) => await callFunctionSafe("editScheduledPost", newPost),
      invalidatesTags: ["Post"],
    }),
    countPosts: builder.query<number, { where: FilterPostsInputs }>({
      queryFn: ({ where }) =>
        executeQuery(async () => {
          if (where.isScheduled) {
            return scheduledPostRepository.count();
          }
          const filter = buildPostQuery(where);

          return postRepository.count(filter);
        }),
      providesTags: ["Post"],
    }),
    deletePostById: builder.mutation<void, string>({
      queryFn: (id) => executeQuery(() => postRepository.delete(id)),
      invalidatesTags: ["Post"],
    }),
    deleteScheduledPost: builder.mutation<void, string>({
      queryFn: async (id) => await callFunctionSafe("deleteScheduledPost", { id }),
      invalidatesTags: ["Post"],
    }),
    createPost: builder.mutation<void, CreatePostFormData>({
      queryFn: (dto) =>
        executeQuery(async () => {
          console.log(dto);
          const postId = generateDocId("posts");

          // Step 1: Upload media files to Firebase Storage
          const attachments = dto.mediaFiles?.length
            ? await uploadFilesToFirebase(dto.mediaFiles, `posts/${postId}`)
            : undefined;
          if (dto.scheduledDate) {
            console.log(new Date(dto.scheduledDate).toUTCString());
            console.log({
              id: postId,
              content: dto.content,
              attachments: attachments ?? [],
              langCodes: dto.langCodes,
              scheduledDate: new Date(dto.scheduledDate).toUTCString(),
            });
            await callFunctionSafe("createScheduledPost", {
              id: postId,
              content: dto.content,
              attachments: attachments ?? [],
              langCodes: dto.langCodes,
              scheduledDate: new Date(dto.scheduledDate).toUTCString(),
            });
          } else {
            const basePost: Omit<BasePost, "id" | "role"> = {
              content: dto.content,
              createdAt: Timestamp.now(),
              langCodes: dto.langCodes as UserLangCode[],
              status: "PUBLISHED",
              updatedAt: Timestamp.now(),
              userId: TAALY,
              vibeCounts: {
                HAHA: 0,
                LOVE: 0,
                NICE: 0,
                SAD: 0,
                WOW: 0,
              },
              visibility: "PUBLIC",
              attachments,
            };
            // Step 2: Create the post with the attachment URLs
            await postRepository.setById(postId, { type: "POST", ...basePost });
          }
        }),
      invalidatesTags: ["Post"],
    }),
  }),
});

export const {
  useCountPostsQuery,
  useGetPostsQuery,
  useDeletePostByIdMutation,
  useCreatePostMutation,
  useEditScheduledPostMutation,
  useGetPostByIdQuery,
  useDeleteScheduledPostMutation,
} = postService;
